import { useState, FormEvent, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { SelectOption } from "@idw-types/common";
import { AxnAttributeLabel, AxnAttributeType } from "@idw-types/data/reference-data/axn";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { AttributeTypeSelectFormGroup } from "../../common/select-form-groups";
import { resources } from "@constants/hardcoded-strings";
import { createAttributeLabelReduxRequest } from "@redux-requests/attribute-labels";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";

type AddAttributeLabelModalProps = {
    onModalClose: () => void;
    axnAttributeTypes: Array<AxnAttributeType>;
};

type AddAttributeLabelModalState = {
    requestInProgress: boolean;
    attributeLabel: AxnAttributeLabel;
    selectedAttributeTypeOption: SelectOption;
};

const defaultAttributeLabel: AxnAttributeLabel = {
    id: "",
    label: "",
    attributeTypeID: ""
};


export const AddAttributeLabelModal = (props: AddAttributeLabelModalProps) => {
    const dispatch = useDispatch();
    const { onModalClose, axnAttributeTypes } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddAttributeLabelModalState>({
        requestInProgress: false,
        attributeLabel: defaultAttributeLabel,
        selectedAttributeTypeOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        const newAttributeLabel = await createAttributeLabelReduxRequest(dispatch)(state.attributeLabel, axnAttributeTypes);
        if (newAttributeLabel) {
            onModalClose();
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUpdateLabel = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            attributeLabel: {
                ...state.attributeLabel,
                label: event.target.value
            }
        });
    };

    const handleUpdateAttributeTypeOptions = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                attributeLabel: {
                    ...state.attributeLabel,
                    attributeTypeID: newValue.value,
                },
                selectedAttributeTypeOption: newValue
            });
        }
    };

    return (
        <DefaultModal
            automationId="add-attribute-label-modal"
            ariaLabelledBy="add-attribute-label-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-attribute-label-title"} closeButton>
                    <Modal.Title>{resources.modals.addAttributeLabel.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputFormGroup
                        controlId="attribute-label-label-input"
                        label={resources.common.inputs.attributeLabel.label}
                        ariaLabel={resources.common.inputs.attributeLabel.label}
                        automationId="attribute-label-label-input"
                        placeholder={resources.common.inputs.attributeLabel.placeholder}
                        value={state.attributeLabel.label}
                        onChange={handleUpdateLabel}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    <AttributeTypeSelectFormGroup
                        idPrefix="management-console"
                        onChange={handleUpdateAttributeTypeOptions}
                        axnAttributeTypes={axnAttributeTypes}
                        selectedOption={state.selectedAttributeTypeOption}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-attribute-label-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-attribute-label-save-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};